import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { format, parse, isValid } from "date-fns";
import { de } from "date-fns/locale";
import { Clock, CalendarIcon } from "lucide-react";

const parseFormDate = (str) => {
  if (!str) return undefined;
  const d = parse(str, "yyyy-MM-dd", new Date());
  return isValid(d) ? d : undefined;
};

export const ManualTimeDialog = ({ open, onOpenChange, form, setForm, onSubmit }) => {
  const selectedDate = parseFormDate(form.date);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="manual-time-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Barlow Condensed" }}>
            Zeiterfassung nachtragen
          </DialogTitle>
          <DialogDescription className="sr-only">
            Manuelles Hinzufügen eines Arbeitszeit-Eintrags mit Datum, Beginn, Ende und optionaler Pause
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label>Datum</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !selectedDate && "text-muted-foreground"
                  )}
                  data-testid="manual-time-date"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, "EEEE, dd. MMMM yyyy", { locale: de }) : <span>Datum wählen</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  locale={de}
                  selected={selectedDate}
                  onSelect={(date) => {
                    if (date) setForm(f => ({ ...f, date: format(date, "yyyy-MM-dd") }));
                  }}
                  weekStartsOn={1}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Beginn</Label>
              <Input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm(f => ({ ...f, start_time: e.target.value }))}
                data-testid="manual-time-start"
              />
            </div>
            <div className="space-y-2">
              <Label>Ende</Label>
              <Input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm(f => ({ ...f, end_time: e.target.value }))}
                data-testid="manual-time-end"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Pause (Minuten)</Label>
            <Input
              type="number"
              min="0"
              value={form.pause_minutes}
              onChange={(e) => setForm(f => ({ ...f, pause_minutes: parseInt(e.target.value) || 0 }))}
              data-testid="manual-time-pause"
            />
          </div>
          <div className="space-y-2">
            <Label>Notiz (optional)</Label>
            <Input
              value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="z.B. Nachträglich erfasst"
              data-testid="manual-time-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={onSubmit} className="gap-2" data-testid="manual-time-submit">
            <Clock className="w-4 h-4" />
            Nachtragen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
